import {Badge, Button} from '@tyrannosaurustech/ui';
import {useMemo} from 'react';
import {Link} from 'react-router-dom';
import {RequestType, RequestTypeLabelMapping} from '../../../../components/atoms/RequestTypes';
import {ParagraphS, SubtitlesS} from '../../../../components/atoms/Typography';
import {Theme} from '../../../../components/atoms/theme';
import {MiniStatusBadge} from '../../../../components/molecules/MiniStatusBadge';
import {CloudUploadIcon, CheckCircleIcon} from '@heroicons/react/outline';
import {useState} from 'react';
import {ModalWithTwoButtons} from '../../../../components/molecules/Modal';
import {useUploadFileVineRecordMutation} from '../../../../redux/reduxQuery/fileVineApi';
import useToast from '../../../../hooks/useToast';
import {formatDate} from '../../../../utils/formatDate';
import {statusColor} from '../../../../utils/statusColor';
import {statusLabel} from '../../../../utils/statusLabel';

const status: selectProps[] = [
  {
    value: 'New',
    label: 'New',
  },
  {
    value: 'Entered',
    label: 'Entered',
  },
  {
    value: 'Processing',
    label: 'Processing',
  },
  {
    value: 'awaiting_law_firm_info',
    label: 'Awaiting Law Firm Info',
  },
  {
    value: 'vetted',
    label: 'Vetted',
  },
  {
    value: 'closed_not_sent',
    label: 'Closed - Not Sent',
  },
  {
    value: 'supervisor_review',
    label: 'Supervisor Review',
  },
  {
    value: 'needs_further_review',
    label: 'Needs Further Review',
  },
  {
    value: 'hold_requests_from_ciox',
    label: 'Hold Requests from Ciox',
  },
];

const providerStatus = [
  {
    value: 'verified',
    label: 'Verified',
  },
  {
    value: 'unverified',
    label: 'Unverified',
  },
  {
    value: 'outdated',
    label: 'Outdated',
  },
];

interface selectProps {
  value: string;
  label: string;
}

const getRequestListTooltip = ({requests}) => {
  return `<ul>
    ${requests.map((request: string, index: number) => `<li key={${request}-${index}}>${request}</li>`).join('')}
  </ul>`;
};

const FVIcon = ({patient, refetch}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadToFileVine] = useUploadFileVineRecordMutation();
  const toast = useToast();
  const synced = !!patient?.fileVineID;

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (synced) window.open(`https://rayna.filevineapp.com/#/addressbook/8443/contact/${patient.fileVineID}`, '_blank');
          else setOpen(true);
        }}
        className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full border-0 bg-transparent cursor-pointer transition-colors hover:bg-gray-200"
        title={synced ? 'View in FileVine' : 'Upload to FileVine'}
      >
        {synced ? (
          <CheckCircleIcon width={16} height={16} color={Theme.colors.success_000} />
        ) : (
          <CloudUploadIcon width={16} height={16} color={Theme.colors.warning_000} />
        )}
      </button>
      {open && (
        <ModalWithTwoButtons
          title="Upload Patient to FileVine"
          content={`Are you sure you want to upload ${patient?.name}'s information to FileVine?`}
          button1Text="Cancel"
          button2Text="Upload"
          loader={loading}
          onClick1={() => setOpen(false)}
          onClick2={() => {
            setLoading(true);
            uploadToFileVine({id: patient.id, type: 'patient'})
              .unwrap()
              .then(() => {
                toast.success(`Successfully uploaded ${patient.name} to FileVine`);
                refetch();
              })
              .catch((err) => {
                const msg = err?.message || err?.data?.message || JSON.stringify(err);
                toast.error(`Unable to upload ${patient.name} to FileVine: ${msg}`);
              })
              .finally(() => {
                setOpen(false);
                setLoading(false);
              });
          }}
        />
      )}
    </>
  );
};

export const Status = (props) => {
  return (
    <ParagraphS color={statusColor[props.status]} style={{margin: 0, textTransform: 'capitalize'}}>
      {statusLabel[props.status]}
    </ParagraphS>
  );
};

export const useColumns = (medicalProvider, patients, refetch) => {
  return {
    columns: useMemo(
      () => [
        {
          id: 'firmName',
          Header: 'Law Firm',
          accessor: 'firm.name',
          header: 'Law Firm',
          accessorKey: 'firm.name',
          size: 200,
          filterName: 'Law Firm',
          includeToolTip: true,
        },
        {
          Header: 'Patient Name',
          accessor: 'patient.name',
          header: 'Patient Name',
          accessorKey: 'patient.name',
          size: 200,
          filterName: 'patientName',
          options: patients,
          cell: (cell) => (
            <div className="flex items-center gap-1">
              <span className="truncate">{cell.row.original.patient?.name}</span>
              <FVIcon patient={cell.row.original.patient} refetch={refetch} />
            </div>
          ),
        },
        {
          Header: 'Status',
          accessor: 'status',
          header: 'Status',
          accessorKey: 'status',
          size: 200,
          filterName: 'status',
          options: status,
          cell: (cell) => <Status status={cell.row.original.status} />,
        },
        {
          Header: 'Requests',
          accessor: (row) => row?.original?.medicalRequests?.length ?? 0,
          header: 'Requests',
          options: RequestType,
          accessorKey: '',
          size: 50,
          cell: (cellProps) => {
            //Order the requests by the order in the RequestTypeLabelMapping (client requested order)
            const requests = Object.keys(RequestTypeLabelMapping).map((type) => {
              const request = cellProps?.row?.original?.medicalRequests.find((r) => r.requestType === type);
              if (request) {
                return RequestTypeLabelMapping[request.requestType];
              }
              return null;
            });

            const sortedRequests = requests.filter((r) => r);
            return (
              <div>
                <div
                  id={'T' + cellProps.row.original.id}
                  className="w-max"
                  data-tooltip-id="request-tooltip"
                  data-tooltip-html={getRequestListTooltip({
                    requests: sortedRequests,
                  })}
                >
                  <Badge icon="EyeIcon" color={Theme.colors.primaries_000} rounded={true} label={`${sortedRequests?.length ?? 0}`} />
                </div>
              </div>
            );
          },
        },
        {
          Header: 'Medical Provider',
          accessor: 'provider.name',
          header: 'Medical Provider',
          accessorKey: 'provider.name',
          filterName: 'providerName',
          options: medicalProvider,
          includeToolTip: true,
          size: 200,
          cell: (cell) => {
            return (
              <>
                <SubtitlesS>{cell.row.original.provider.name}</SubtitlesS>
                <ParagraphS color={Theme.colors.gray_000}>{cell.row.original.provider.address}</ParagraphS>
              </>
            );
          },
        },
        {
          Header: 'Verif.',
          id: 'Verif',
          filterName: 'providerStatus',
          header: 'Verif.',
          accessorKey: 'providerStatus',
          options: providerStatus,
          accessor: (row) => row?.original?.provider?.status,
          size: 100,
          cell: (cell) => {
            return (
              <div>
                <MiniStatusBadge id={'Verified-' + cell.row.original.id} status={cell.row.original.provider.status} />
              </div>
            );
          },
        },
        {
          Header: 'Service Dates',
          filterName: 'serviceDates',
          header: 'Service Dates',
          accessorKey: 'serviceDates',
          size: 150,
          cell: (cell) => {
            const row = cell.row.original;
            if (row.serviceDateFrom && row.serviceDateTo) {
              return `${formatDate(row.serviceDateFrom)} - ${formatDate(row.serviceDateTo)}`;
            }
            return formatDate(row.serviceDates);
          },
        },
        {
          // Explicit id so react-table v7 (Filter sidebar) doesn't fall back to the
          // Header-derived id, which would miss the submittedDate filter mapping
          id: 'submittedDate',
          Header: 'Submitted',
          filterName: 'submittedDate',
          header: 'Submitted',
          accessorKey: 'submittedDate',
          size: 100,
          accessor: (row) => formatDate(row.submittedDate),
          cell: (cell) => {
            return formatDate(cell.row.original.submittedDate);
          },
          disableFilters: true,
        },
        {
          Header: 'Actions',
          accessor: 'id',
          header: '',
          accessorKey: 'id',
          disableFilters: true,
          enableSorting: false,
          size: 72,
          cell: (cell) => {
            return (
              <div className="mr-[1rem]">
                <Link to={`${cell.row.original.id}`}>
                  <Button icon="EyeIcon"></Button>
                </Link>
              </div>
            );
          },
        },
      ],
      [medicalProvider, patients, refetch],
    ),
  };
};
